// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, CircularProgress, Alert, Grid, Tabs, Tab,
  Switch, FormControlLabel, Divider, Card, CardContent, Accordion, AccordionSummary, AccordionDetails, IconButton, MenuItem, InputLabel, FormControl, Select, Tooltip
} from '@mui/material';
import { 
  Add as AddIcon, CheckCircle as CheckCircleIcon, ExpandMore as ExpandMoreIcon, 
  Delete as DeleteIcon, Visibility as VisibilityIcon, Print as PrintIcon, 
  Email as EmailIcon 
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import { useReactToPrint } from 'react-to-print';
import IndustrialPayslip from '../components/Payroll/IndustrialPayslip';

const PayrollPage = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [staff, setStaff] = useState<any[]>([]);
  const [payslips, setPayslips] = useState<any[]>([]);
  const [structures, setStructures] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [school, setSchool] = useState<any>(null);
  
  const [summary, setSummary] = useState<any>(null);

  // Dialog States
  const [openRunDialog, setOpenRunDialog] = useState(false);
  const [openStructureDialog, setOpenStructureDialog] = useState(false);
  const [openEnrollDialog, setOpenEnrollDialog] = useState(false); 
  const [openPayslipDialog, setOpenPayslipDialog] = useState(false);
  const [viewingPayslip, setViewingPayslip] = useState<any>(null);

  // Print Setup
  const payslipRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
      contentRef: payslipRef
  });

  const defaultTaxConfig = {
    nssaPercentage: 4.5, nssaCeiling: 700, aidsLevyPercentage: 3,
    payeBrackets: [
        { min: 0, max: 100, rate: 0 }, { min: 100, max: 300, rate: 20 },
        { min: 300, max: 1000, rate: 25 }, { min: 1000, max: 2000, rate: 30 },
        { min: 2000, max: 3000, rate: 35 }, { min: 3000, max: '', rate: 40 }
    ]
  };

  const [runData, setRunData] = useState({ month: new Date().getMonth() + 1, year: new Date().getFullYear(), applyZimTaxes: true, taxConfig: JSON.parse(JSON.stringify(defaultTaxConfig)) });
  const [structureData, setStructureData] = useState({ name: '', basicSalary: 0, allowances: [], deductions: [] });
  const [enrollData, setEnrollData] = useState({ staffId: '', salaryStructureId: '' });

  const fetchData = async () => {
    try {
      setLoading(true);
      const userInfo = localStorage.getItem('userInfo');
      const token = userInfo ? JSON.parse(userInfo).token : null;
      const headers = { Authorization: `Bearer ${token}` };
      
      const [staffRes, payslipsRes, summaryRes, structRes] = await Promise.all([
        axios.get(getApiUrl('payroll/staff'), { headers }),
        axios.get(getApiUrl('payroll/payslips'), { headers }),
        axios.get(getApiUrl('payroll/summary'), { headers }),
        axios.get(getApiUrl('payroll/structures'), { headers })
      ]);
      
      setStaff(staffRes.data);
      setPayslips(Array.isArray(payslipsRes.data) ? payslipsRes.data : []);
      setSummary(summaryRes.data);
      setStructures(structRes.data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSchool = async () => {
    try {
      const userInfo = localStorage.getItem('userInfo');
      const token = userInfo ? JSON.parse(userInfo).token : null;
      const { data } = await axios.get(getApiUrl('school'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchool(data);
    } catch (err) {
      console.error('Error fetching school profile:', err);
    }
  };

  useEffect(() => {
    fetchData();
    fetchSchool();
  }, []);

  const handleRunPayroll = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const userInfo = localStorage.getItem('userInfo');
      const token = userInfo ? JSON.parse(userInfo).token : null;
      const headers = { Authorization: `Bearer ${token}` };
      const payload = { ...runData, taxConfig: { ...runData.taxConfig } };
      payload.taxConfig.payeBrackets = payload.taxConfig.payeBrackets.map(b => ({
          ...b, max: b.max === '' ? null : b.max
      }));

      await axios.post(getApiUrl('payroll/run'), payload, { headers });
      setOpenRunDialog(false);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to run payroll');
    }
  };

  const handleNotifySalary = async (id: string, name: string) => {
      try {
          const userInfo = localStorage.getItem('userInfo');
          const token = userInfo ? JSON.parse(userInfo).token : null;
          await axios.post(getApiUrl(`payroll/staff/${id}/notify`), {}, {
              headers: { Authorization: `Bearer ${token}` }
          });
          setSuccess(`Remuneration details sent to ${name}`);
      } catch (err: any) {
          setError(err.response?.data?.message || 'Failed to send notification');
      }
  };

  const handleApprove = async (id: string) => {
    try {
      const userInfo = localStorage.getItem('userInfo');
      const token = userInfo ? JSON.parse(userInfo).token : null;
      await axios.put(getApiUrl(`payroll/payslips/${id}/approve`), {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to post to ledger');
    }
  };

  const handleCreateStructure = async (e: React.FormEvent) => {
      e.preventDefault();
      try {
          const userInfo = localStorage.getItem('userInfo');
          const token = userInfo ? JSON.parse(userInfo).token : null;
          await axios.post(getApiUrl('payroll/structures'), structureData, { headers: { Authorization: `Bearer ${token}` } });
          setOpenStructureDialog(false);
          setStructureData({ name: '', basicSalary: 0, allowances: [], deductions: [] });
          fetchData();
      } catch(err: any) {
          setError(err.response?.data?.message || 'Failed to create Salary Structure');
      }
  };

  const handleEnrollStaff = async (e: React.FormEvent) => {
      e.preventDefault();
      try {
          const userInfo = localStorage.getItem('userInfo');
          const token = userInfo ? JSON.parse(userInfo).token : null;
          await axios.put(getApiUrl(`payroll/staff/${enrollData.staffId}`), { salaryStructureId: enrollData.salaryStructureId }, { headers: { Authorization: `Bearer ${token}` } });
          setSuccess('Personnel successfully mapped to salary plan. Notification dispatched.');
          setOpenEnrollDialog(false);
          fetchData();
      } catch(err: any) {
          setError(err.response?.data?.message || 'Failed to assign salary plan');
      }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PAID': return 'success';
      case 'APPROVED': return 'info';
      case 'DRAFT': return 'warning';
      default: return 'default';
    }
  };

  const openPayslipViewer = (slip: any) => {
      setViewingPayslip(slip);
      setOpenPayslipDialog(true);
  };

  const addIncome = () => { setStructureData({...structureData, allowances: [...structureData.allowances, { name: '', amount: 0 }]}); }
  const removeIncome = (i: number) => { setStructureData({...structureData, allowances: structureData.allowances.filter((_, idx)=>idx!==i)}); }
  const handleIncomeChange = (i: number, field: string, val: any) => {
      const updated = [...structureData.allowances];
      updated[i][field] = val;
      setStructureData({...structureData, allowances: updated});
  }

  const addDeduction = () => { setStructureData({...structureData, deductions: [...structureData.deductions, { name: '', amount: 0 }]}); }
  const removeDeduction = (i: number) => { setStructureData({...structureData, deductions: structureData.deductions.filter((_, idx)=>idx!==i)}); }
  const handleDeductionChange = (i: number, field: string, val: any) => {
      const updated = [...structureData.deductions];
      updated[i][field] = val;
      setStructureData({...structureData, deductions: updated});
  }

  const chartData = [ { name: 'Month Summary', Gross: (summary?.totalBasicSalary || 0) + (summary?.totalAllowances || 0), NetPay: summary?.totalNetSalary || 0, Deductions: summary?.totalDeductions || 0 } ];

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 2, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
            <Typography variant="h4" fontWeight={900} color="#1A202C">Payroll Management</Typography>
            <Typography variant="subtitle1" color="text.secondary">Comprehensive financial compensations and statutory filings.</Typography>
        </Box>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setOpenRunDialog(true)} sx={{ borderRadius: '8px', fontWeight: 800, px: 3 }}>
          Run Monthly Payroll
        </Button>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 4, borderRadius: '8px', fontWeight: 600 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 4, borderRadius: '8px', fontWeight: 600 }}>{success}</Alert>}

      {summary && (
        <Grid container spacing={4} sx={{ mb: 5 }}>
            <Grid item xs={12} md={4}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 3, bgcolor: '#ebf8fa' }}>
                                <Typography variant="subtitle2" color="#4A5568" fontWeight={700} mb={1}>TOTAL NET EXPENDITURE</Typography>
                                <Typography variant="h3" fontWeight={900} color="#00A3C4">
                                ${Number(summary.totalNetSalary).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                            <CardContent>
                                <Typography variant="caption" color="text.secondary" fontWeight={700}>STATUTORY + DEDUCTIONS</Typography>
                                <Typography variant="h6" fontWeight={800} color="#E53E3E">
                                ${Number(summary.totalDeductions).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                            <CardContent>
                                <Typography variant="caption" color="text.secondary" fontWeight={700}>ALLOWANCES ALLOWED</Typography>
                                <Typography variant="h6" fontWeight={800} color="#38A169">
                                ${Number(summary.totalAllowances).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12} md={8}>
                <Paper sx={{ p: 3, borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: 'none', height: 260 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} layout="vertical" margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                            <XAxis type="number" tickFormatter={(v) => `$${v}`} />
                            <YAxis dataKey="name" type="category" width={100} />
                            <RechartsTooltip formatter={(value) => `$${Number(value).toLocaleString()}`} />
                            <Legend />
                            <Bar dataKey="Gross" fill="#4299E1" radius={[0, 4, 4, 0]} barSize={20} />
                            <Bar dataKey="NetPay" name="Net Pay" fill="#48BB78" radius={[0, 4, 4, 0]} barSize={20} />
                            <Bar dataKey="Deductions" fill="#E53E3E" radius={[0, 4, 4, 0]} barSize={20} />
                        </BarChart>
                    </ResponsiveContainer>
                </Paper>
            </Grid>
        </Grid>
      )}

      <Paper sx={{ mb: 4, borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: 'none' }}>
        <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)} sx={{ borderBottom: 1, borderColor: '#E2E8F0', px: 2 }}>
          <Tab label="Payslips & Journals" sx={{ fontWeight: 800 }} />
          <Tab label="Salary Structures" sx={{ fontWeight: 800 }} />
          <Tab label="Payroll Enrollment" sx={{ fontWeight: 800 }} />
        </Tabs>
      </Paper>

      {tabIndex === 0 && (
        <TableContainer component={Paper} sx={{ borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: 'none' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                <TableCell sx={{ fontWeight: 800 }}>Period</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Staff Payee</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>Gross Amt</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>Deductions</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>Net Pay</TableCell>
                <TableCell align="center" sx={{ fontWeight: 800 }}>Status</TableCell>
                <TableCell align="center" sx={{ fontWeight: 800 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payslips.map((payslip) => (
                <TableRow key={payslip.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{payslip.month}/{payslip.year}</TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight={800}>{payslip.staffProfile?.user?.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{(payslip.breakdown?.deductions?.filter((d:any)=>d.isStatutory).length > 0) ? '+ Statutory Executed' : ''}</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>${Number(payslip.breakdown?.grossSalary || payslip.basicSalary).toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                  <TableCell align="right" sx={{ color: '#E53E3E' }}>${Number(payslip.totalDeductions).toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 900, color: '#38A169' }}>${Number(payslip.netSalary).toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                  <TableCell align="center"><Chip label={payslip.status} color={getStatusColor(payslip.status) as any} size="small" sx={{ fontWeight: 800, borderRadius: '6px' }} /></TableCell>
                  <TableCell align="center">
                    <Button size="small" variant="text" color="primary" onClick={() => openPayslipViewer(payslip)} sx={{ minWidth:0, mr:1 }}><VisibilityIcon fontSize="small" /></Button>
                    {payslip.status === 'DRAFT' && (
                      <Button size="small" variant="contained" color="secondary" onClick={() => handleApprove(payslip.id)} sx={{ borderRadius: '6px', fontWeight:600 }}>Post Ledger</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {payslips.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6 }}><Typography color="text.secondary">No payslips executed.</Typography></TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tabIndex === 1 && (
         <Box>
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setOpenStructureDialog(true)}>Create Salary Plan</Button>
            </Box>
            <Grid container spacing={3}>
                {structures.map((s) => (
                    <Grid item xs={12} sm={6} md={4} key={s.id}>
                        <Card sx={{ borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                            <CardContent>
                                <Typography variant="h6" fontWeight={800}>{s.name}</Typography>
                                <Divider sx={{ my: 1.5 }} />
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography variant="body2" color="text.secondary">Basic Base</Typography>
                                    <Typography variant="body2" fontWeight={700}>${Number(s.basicSalary).toFixed(2)}</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography variant="body2" color="text.secondary">Incomes Mapped</Typography>
                                    <Typography variant="body2" fontWeight={700}>{(s.allowances as any)?.length || 0}</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2" color="text.secondary">Deductions Mapped</Typography>
                                    <Typography variant="body2" fontWeight={700}>{(s.deductions as any)?.length || 0}</Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
         </Box>
      )}

      {tabIndex === 2 && (
        <TableContainer component={Paper} sx={{ borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: 'none' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                <TableCell sx={{ fontWeight: 800 }}>Employee ID</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Full Name</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Position & Dept</TableCell>
                <TableCell align="center" sx={{ fontWeight: 800 }}>Payment Method</TableCell>
                <TableCell align="center" sx={{ fontWeight: 800 }}>Salary Plan Required</TableCell>
                <TableCell align="center" sx={{ fontWeight: 800 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {staff.map((employee) => {
                  const hasPlan = !!employee.salaryStructureId;
                  return (
                <TableRow key={employee.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{employee.employeeNo}</TableCell>
                  <TableCell>{employee.user?.name}</TableCell>
                  <TableCell>{employee.position || '-'} ({employee.department || '-'})</TableCell>
                  <TableCell align="center">
                      <Chip 
                        label={employee.preferredPaymentMethod || 'CASH'} 
                        size="small" 
                        variant="outlined" 
                        color="secondary"
                        sx={{ fontWeight: 800, fontSize: 10 }} 
                      />
                  </TableCell>
                  <TableCell align="center">
                    {hasPlan ? (
                       <Chip label="Configured" color="success" size="small" icon={<CheckCircleIcon />} sx={{ fontWeight:700 }} />
                    ) : (
                       <Chip label="Missing Config" color="error" size="small" variant="outlined" sx={{ fontWeight:700 }} />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={1} justifyContent="center">
                      <Button size="small" variant="outlined" onClick={() => { setEnrollData({ staffId: employee.id, salaryStructureId: employee.salaryStructureId || '' }); setOpenEnrollDialog(true); }}>
                        {hasPlan ? 'Re-enroll' : 'Map Salary Plan'}
                      </Button>
                      {hasPlan && (
                        <Tooltip title="Notify via Email"><IconButton size="small" color="primary" onClick={() => handleNotifySalary(employee.id, employee.user?.name)}><EmailIcon fontSize="small" /></IconButton></Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )})}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openRunDialog} onClose={() => setOpenRunDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' }}}>
        <DialogTitle sx={{ px: 4, py: 3, fontWeight: 900, borderBottom: '1px solid #E2E8F0' }}>Process Payroll Batch</DialogTitle>
        <form onSubmit={handleRunPayroll}>
          <DialogContent sx={{ px: 4, py: 3, bgcolor: '#FAFAF9' }}>
            <Box mb={4}>
                <Typography variant="subtitle2" fontWeight={800} color="text.secondary" mb={2}>PERIOD SELECTION</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}><TextField fullWidth label="Month (1-12)" type="number" inputProps={{ min: 1, max: 12 }} value={runData.month} onChange={(e) => setRunData({ ...runData, month: Number(e.target.value) })} required sx={{ bgcolor: 'white' }} /></Grid>
                    <Grid item xs={12} sm={6}><TextField fullWidth label="Year" type="number" value={runData.year} onChange={(e) => setRunData({ ...runData, year: Number(e.target.value) })} required sx={{ bgcolor: 'white' }} /></Grid>
                </Grid>
            </Box>

            <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle2" fontWeight={800} color="text.secondary">STATUTORY TAXATION ENGINE (ZIMRA / NSSA)</Typography>
                    <FormControlLabel control={<Switch checked={runData.applyZimTaxes} onChange={(e) => setRunData({ ...runData, applyZimTaxes: e.target.checked })} color="secondary" />} label={<Typography fontWeight={700}>Apply Tax Engine</Typography>} />
                </Box>
                
                {runData.applyZimTaxes && (
                    <Accordion sx={{ borderRadius: '8px', border: '1px solid #E2E8F0', boxShadow: 'none', '&:before': { display: 'none' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'white', borderBottom: '1px solid #E2E8F0' }}>
                           <Typography fontWeight={700} color="#2B6CB0">Advanced Tax Bracket Settings (Editable)</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ bgcolor: 'white', p: 3 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}><TextField fullWidth label="NSSA Levy (%)" type="number" inputProps={{ step: "0.1" }} value={runData.taxConfig.nssaPercentage} onChange={(e) => setRunData({ ...runData, taxConfig: { ...runData.taxConfig, nssaPercentage: e.target.value } })} size="small" /></Grid>
                                <Grid item xs={12} sm={6}><TextField fullWidth label="NSSA Insurable Gross Ceiling (USD)" type="number" value={runData.taxConfig.nssaCeiling} onChange={(e) => setRunData({ ...runData, taxConfig: { ...runData.taxConfig, nssaCeiling: e.target.value } })} size="small" /></Grid>
                                <Grid item xs={12} sm={6}><TextField fullWidth label="AIDS Levy (%)" type="number" inputProps={{ step: "0.1" }} value={runData.taxConfig.aidsLevyPercentage} onChange={(e) => setRunData({ ...runData, taxConfig: { ...runData.taxConfig, aidsLevyPercentage: e.target.value } })} size="small" /></Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 4, pt: 3, borderTop: '1px solid #E2E8F0' }}>
            <Button onClick={() => setOpenRunDialog(false)} sx={{ fontWeight: 600 }}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary" sx={{ borderRadius: '8px', fontWeight: 800, px: 3 }}>Process Batch Drafts</Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={openStructureDialog} onClose={() => setOpenStructureDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' }}}>
        <DialogTitle sx={{ px:4, py:3, fontWeight: 900, borderBottom: '1px solid #E2E8F0' }}>Create Salary Plan Template</DialogTitle>
        <form onSubmit={handleCreateStructure}>
          <DialogContent sx={{ px: 4, py: 3 }}>
            <TextField fullWidth label="Structure Name (e.g. Senior Teacher Scale)" required value={structureData.name} onChange={(e) => setStructureData({...structureData, name: e.target.value})} sx={{ mb: 3 }} />
            <TextField fullWidth label="Fixed Basic Salary ($)" type="number" required value={structureData.basicSalary} onChange={(e) => setStructureData({...structureData, basicSalary: Number(e.target.value)})} sx={{ mb: 4 }} />
            
            <Typography variant="subtitle2" fontWeight={800} mb={1}>ADDITIONAL EARNINGS & ALLOWANCES</Typography>
            {structureData.allowances.map((al: any, i: number) => (
                <Box key={`al-${i}`} display="flex" gap={2} mb={2}>
                    <TextField label="Allowance Name (e.g. Housing)" size="small" sx={{ flex: 1 }} value={al.name} onChange={(e) => handleIncomeChange(i, 'name', e.target.value)} required />
                    <TextField label="Amount ($)" type="number" size="small" sx={{ flex: 0.5 }} value={al.amount} onChange={(e) => handleIncomeChange(i, 'amount', Number(e.target.value))} required />
                    <IconButton color="error" onClick={() => removeIncome(i)}><DeleteIcon fontSize="small" /></IconButton>
                </Box>
            ))}
            <Button size="small" startIcon={<AddIcon />} variant="outlined" onClick={addIncome} sx={{ mb: 4 }}>Add Allowance Income</Button>

            <Typography variant="subtitle2" fontWeight={800} mb={1}>NON-STATUTORY DEDUCTIONS</Typography>
            {structureData.deductions.map((d: any, i: number) => (
                <Box key={`de-${i}`} display="flex" gap={2} mb={2}>
                    <TextField label="Deduction Name" size="small" sx={{ flex: 1 }} value={d.name} onChange={(e) => handleDeductionChange(i, 'name', e.target.value)} required />
                    <TextField label="Amount ($)" type="number" size="small" sx={{ flex: 0.5 }} value={d.amount} onChange={(e) => handleDeductionChange(i, 'amount', Number(e.target.value))} required />
                    <IconButton color="error" onClick={() => removeDeduction(i)}><DeleteIcon fontSize="small" /></IconButton>
                </Box>
            ))}
            <Button size="small" startIcon={<AddIcon />} variant="outlined" onClick={addDeduction}>Add Custom Deduction</Button>
          </DialogContent>
          <DialogActions sx={{ p: 4, pt: 3, borderTop: '1px solid #E2E8F0' }}>
            <Button onClick={() => setOpenStructureDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary" sx={{ borderRadius: '8px', fontWeight: 700 }}>Save Template</Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={openEnrollDialog} onClose={() => setOpenEnrollDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' }}}>
        <DialogTitle sx={{ px:4, py:2, fontWeight: 900, borderBottom: '1px solid #E2E8F0' }}>Map Personnel to Salary Plan</DialogTitle>
        <form onSubmit={handleEnrollStaff}>
          <DialogContent sx={{ px: 4, py: 3 }}>
            <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Select Salary Structure</InputLabel>
                <Select value={enrollData.salaryStructureId} label="Select Salary Structure" onChange={(e) => setEnrollData({ ...enrollData, salaryStructureId: e.target.value })} required>
                    {structures.map(s => <MenuItem key={s.id} value={s.id}>{s.name} (${Number(s.basicSalary).toFixed(2)})</MenuItem>)}
                </Select>
            </FormControl>
            <Box mt={3}>
                <Alert severity="info" sx={{ fontSize: 11 }}>Upon saving, the employee will receive an automated email with their remuneration breakdown.</Alert>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: '1px solid #E2E8F0' }}>
            <Button onClick={() => setOpenEnrollDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary" sx={{ fontWeight: 700 }}>Enroll & Notify Staff</Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={openPayslipDialog} onClose={() => setOpenPayslipDialog(false)} maxWidth="md" fullWidth>
          <DialogContent sx={{ p: 0 }}>
             <IndustrialPayslip ref={payslipRef} payslip={viewingPayslip} tenantSettings={school} />
          </DialogContent>
          <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
              <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint}>Print to PDF</Button>
              <Button onClick={() => setOpenPayslipDialog(false)} variant="contained">Close</Button>
          </DialogActions>
      </Dialog>

    </Box>
  );
};

export default PayrollPage;
